import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { Loader } from '../../../../components';
import {
  formatEnergyAutoUnit,
  formatPowerAutoUnit,
  notifyError,
} from '../../../../utils';
import { getThingEnergy, intiateOperacionalEnergy } from '../../../../services';
import { dateFormats } from '../../../../constants';
import * as s from './styled-overview';

const Overview = ({ thing, values, type }) => {
  const dispatch = useDispatch();

  const { energy } = useSelector((state) => state.operacional);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);

  // type: 'ucha' (turbogerador) | 'eba' (bomba). Antes o codigo usava 'tga'
  // que nunca era setado, entao sempre caia no else (Consumido/consumida)
  // mesmo para UCHAs. Aqui normalizamos para `isGerador`.
  const isGerador = type === 'ucha';
  const accumLabel = isGerador ? 'Acumulado' : 'Consumido';
  const energyLabel = isGerador ? 'gerada' : 'consumida';

  // Convencao: o backend ja retorna o valor com sinal semanticamente
  // coerente com o tipo da maquina:
  //   - UCHA: positivo = gerou energia (operacao normal turbina)
  //   - EBA:  positivo = consumiu energia (operacao normal bomba)
  // Em ambos casos, negativo indica anomalia (UCHA consumindo da rede ou
  // EBA injetando) — e a cor reage automaticamente via prop `negative`.
  const toNumeric = (raw) => {
    if (raw === null || raw === undefined) return null;
    const numeric = Number(raw);
    return Number.isFinite(numeric) ? numeric : null;
  };
  // formatEnergyAutoUnit cuida do escalonamento (kWh/MWh/GWh) e de strings
  // que ja venham com unidade embutida. Aqui so delegamos.
  const formatEnergy = (raw) =>
    raw === null || raw === undefined ? '-' : formatEnergyAutoUnit(raw, 'kWh');
  // Helpers para cor condicional: verde quando operacao normal (>=0),
  // vermelho quando anomalia (<0). Reusa o mesmo padrao do PeriodValue.
  const isAnomaly = (raw) => {
    const numeric = toNumeric(raw);
    return numeric !== null && numeric < 0;
  };

  useEffect(() => {
    let mounted = true;
    if (socketRef.current && typeof socketRef.current.disconnect === 'function') {
      socketRef.current.disconnect();
    }
    getThingEnergy(thing.id)
      .then(({ data }) => {
        dispatch({
          type: 'OPERACIONAL/SET_ENERGY',
          data,
        });
        if (mounted) {
          socketRef.current = intiateOperacionalEnergy(thing.id);
        }
      })
      .catch(() =>
        notifyError('Não foi possível buscar as informações de energia :(')
      )
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
      if (socketRef.current && typeof socketRef.current.disconnect === 'function') {
        socketRef.current.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thing.id]);

  return (
    <s.Container>
      {loading ? (
        <Loader />
      ) : (
        <>
          {/* KPI 1: Ontime Running — sempre cor red (design .kpi.pot .v) */}
          <s.Card>
            <s.Label $title>Ontime Running</s.Label>
            <s.Label $value $color="var(--cco-red)">
              {values && values.PotenciaAtivaMedia !== undefined
                ? formatPowerAutoUnit(values.PotenciaAtivaMedia, 'kW')
                : '-'}
            </s.Label>
          </s.Card>
          {/* KPI 2: Consumido/Acumulado histórico — cor ink (design .kpi.hist) */}
          <s.Card>
            <s.Label $title>{accumLabel} histórico</s.Label>
            <s.Label
              $value
              $color={
                energy && isAnomaly(energy.generatedEnergyHistoric)
                  ? 'var(--cco-red)'
                  : 'var(--cco-ink)'
              }
            >
              {energy ? formatEnergy(energy.generatedEnergyHistoric) : '-'}
            </s.Label>
          </s.Card>
          {/* KPI 3: Total de energia/dia — cor green (design .kpi.dia .v) */}
          <s.Card>
            <s.Label $title>Total de energia {energyLabel}/dia</s.Label>
            <s.Label
              $value
              $color={
                energy &&
                energy.generatedEnergyOfPeriod &&
                isAnomaly(energy.generatedEnergyOfPeriod.day)
                  ? 'var(--cco-red)'
                  : 'var(--cco-green)'
              }
            >
              {energy && energy.generatedEnergyOfPeriod
                ? formatEnergy(energy.generatedEnergyOfPeriod.day)
                : '-'}
            </s.Label>
          </s.Card>
          {/* KPI 4: Última atualização — JetBrains Mono (design .kpi.upd) */}
          <s.Card>
            <s.Label $title>Última atualização</s.Label>
            <s.Label $value $mono>
              {values && values.ts
                ? format(new Date(values.ts), dateFormats.DATE_TIME_SLASH_H)
                : '-'}
            </s.Label>
          </s.Card>
        </>
      )}
    </s.Container>
  );
};

export default Overview;
